<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h5 class="mb-0">角色管理</h5>
              <p class="mb-0 text-sm text-muted">管理角色及其权限</p>
            </div>
            <button class="btn btn-primary btn-sm mb-0" @click="openAdd">+ 新增角色</button>
          </div>
          <div class="table-responsive">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">ID</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">角色名称</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">拥有权限</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">创建时间</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="5" class="text-center py-4 text-sm text-muted">加载中...</td>
                </tr>
                <tr v-for="r in roles" :key="r.id">
                  <td><p class="text-sm font-weight-bold mb-0">{{ r.id }}</p></td>
                  <td><p class="text-sm font-weight-bold mb-0">{{ r.name }}</p></td>
                  <td>
                    <span
                      v-for="p in r.permissions" :key="p"
                      class="badge bg-gradient-info me-1 mb-1"
                    >{{ permName(p) }}</span>
                    <span v-if="!r.permissions || r.permissions.length === 0" class="text-sm text-muted">无权限</span>
                  </td>
                  <td><p class="text-sm mb-0">{{ r.created_at }}</p></td>
                  <td>
                    <button class="btn btn-link btn-sm mb-0 p-0 me-2 text-info" @click="openEdit(r)">编辑</button>
                    <button class="btn btn-link btn-sm mb-0 p-0 text-danger" :disabled="r.id <= 2" @click="confirmDelete(r)">删除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-box card p-4" style="width:460px">
        <h6 class="mb-3">{{ isEdit ? '编辑角色' : '新增角色' }}</h6>
        <div v-if="formError" class="alert alert-danger text-white text-sm py-2 mb-3">{{ formError }}</div>
        <div class="mb-3">
          <label class="form-label text-sm">角色名称</label>
          <input v-model="form.name" type="text" class="form-control" placeholder="请输入角色名称" />
        </div>
        <div class="mb-4">
          <label class="form-label text-sm mb-2">权限配置</label>
          <div class="row">
            <div v-for="p in allPermissions" :key="p.key" class="col-6 mb-2">
              <div class="form-check">
                <input
                  :id="'perm-' + p.key"
                  v-model="form.permissions"
                  class="form-check-input"
                  type="checkbox"
                  :value="p.key"
                />
                <label :for="'perm-' + p.key" class="form-check-label text-sm">{{ p.name }}</label>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-sm btn-secondary mb-0" @click="closeModal">取消</button>
          <button class="btn btn-sm btn-primary mb-0" :disabled="saving" @click="saveRole">
            {{ saving ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 删除确认 -->
    <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
      <div class="modal-box card p-4" style="width:360px">
        <h6 class="mb-2">确认删除</h6>
        <p class="text-sm mb-4">确定要删除角色 <strong>{{ deleteTarget.name }}</strong> 吗？</p>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-sm btn-secondary mb-0" @click="deleteTarget = null">取消</button>
          <button class="btn btn-sm btn-danger mb-0" :disabled="saving" @click="doDelete">确认删除</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from "@/api/index.js";

export default {
  name: "RoleManage",
  data() {
    return {
      loading: true,
      saving: false,
      roles: [],
      allPermissions: [],
      showModal: false,
      isEdit: false,
      form: { id: null, name: "", permissions: [] },
      formError: "",
      deleteTarget: null,
    };
  },
  async created() {
    await Promise.all([this.loadRoles(), this.loadPermissions()]);
  },
  methods: {
    async loadRoles() {
      try {
        const data = await request("/api/roles");
        this.roles = (data || []).sort((a, b) => a.id - b.id);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadPermissions() {
      try {
        const data = await request("/api/permissions");
        this.allPermissions = data || [];
      } catch (e) {
        console.error(e);
      }
    },
    permName(key) {
      return this.allPermissions.find(p => p.key === key)?.name || key;
    },
    openAdd() {
      this.isEdit = false;
      this.form = { id: null, name: "", permissions: [] };
      this.formError = "";
      this.showModal = true;
    },
    openEdit(r) {
      this.isEdit = true;
      this.form = { id: r.id, name: r.name, permissions: [...(r.permissions || [])] };
      this.formError = "";
      this.showModal = true;
    },
    closeModal() { this.showModal = false; },
    async saveRole() {
      this.formError = "";
      if (!this.form.name) { this.formError = "请填写角色名称"; return; }
      this.saving = true;
      try {
        if (this.isEdit) {
          await request(`/api/roles/${this.form.id}`, {
            method: "PUT",
            body: JSON.stringify({ name: this.form.name, permissions: this.form.permissions }),
          });
        } else {
          await request("/api/roles", {
            method: "POST",
            body: JSON.stringify({ name: this.form.name, permissions: this.form.permissions }),
          });
        }
        this.showModal = false;
        await this.loadRoles();
      } catch (e) {
        this.formError = e.message;
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(r) { this.deleteTarget = r; },
    async doDelete() {
      this.saving = true;
      try {
        await request(`/api/roles/${this.deleteTarget.id}`, { method: "DELETE" });
        this.deleteTarget = null;
        await this.loadRoles();
      } catch (e) {
        alert(e.message);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
}
.modal-box { border-radius: 12px; }
</style>
